import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Refresh,
  Delete as DeleteIcon,
  Build as BuildIcon,
  Storage as StorageIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Code as CodeIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { http } from '../lib/http';
import CSVManagement from './CSVManagement';

interface User {
  id: string;
  username: string;
  robloxName: string;
  email?: string;
  role: string;
  status: string;
  createdAt: number;
  lastLogin?: number;
  isActive: boolean;
}

interface PendingUser {
  id: string;
  username: string;
  robloxName: string;
  email?: string;
  status: string;
  createdAt: number;
}

interface RejectedUser {
  id: string;
  username: string;
  robloxName: string;
  email?: string;
  status: string;
  createdAt: number;
  rejectedAt?: number;
}

interface CallCode {
  code: string;
  description: string;
  boxPlan: string;
  priority: number;
}

interface POI {
  id: string;
  code?: string;
  name: string;
  address: string;
  boxLocation: string;
  category: string;
  description?: string;
  coordinates?: {
    x: number;
    y: number;
    z: number;
  };
  isActive: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface AdminInterfaceProps {
  open: boolean;
  onClose: () => void;
}

// Call Code Dialog Component
interface CallCodeDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  callCode: CallCode | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const CallCodeDialog: React.FC<CallCodeDialogProps> = ({ open, mode, callCode, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    boxPlan: '',
    priority: 3
  });

  useEffect(() => {
    if (mode === 'edit' && callCode) {
      setFormData({
        code: callCode.code,
        description: callCode.description,
        boxPlan: callCode.boxPlan,
        priority: callCode.priority
      });
    } else {
      setFormData({
        code: '',
        description: '',
        boxPlan: '',
        priority: 3
      });
    }
  }, [mode, callCode, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: '#eceef1' }}>
        {mode === 'create' ? 'Create Call Code' : 'Edit Call Code'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              required
              disabled={mode === 'edit'}
              sx={{
                '& .MuiInputLabel-root': { color: '#b4b9c0' },
                '& .MuiOutlinedInput-root': {
                  color: '#eceef1',
                  '& fieldset': { borderColor: '#40444b' },
                  '&:hover fieldset': { borderColor: '#3c8cff' },
                  '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                }
              }}
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              required
              multiline
              rows={2}
              sx={{
                '& .MuiInputLabel-root': { color: '#b4b9c0' },
                '& .MuiOutlinedInput-root': {
                  color: '#eceef1',
                  '& fieldset': { borderColor: '#40444b' },
                  '&:hover fieldset': { borderColor: '#3c8cff' },
                  '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                }
              }}
            />
            <TextField
              label="Box Plan"
              value={formData.boxPlan}
              onChange={(e) => setFormData(prev => ({ ...prev, boxPlan: e.target.value }))}
              required
              sx={{
                '& .MuiInputLabel-root': { color: '#b4b9c0' },
                '& .MuiOutlinedInput-root': {
                  color: '#eceef1',
                  '& fieldset': { borderColor: '#40444b' },
                  '&:hover fieldset': { borderColor: '#3c8cff' },
                  '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                }
              }}
            />
            <FormControl>
              <InputLabel sx={{ color: '#b4b9c0' }}>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as number }))}
                label="Priority"
                sx={{
                  color: '#eceef1',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#40444b' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3c8cff' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3c8cff' }
                }}
              >
                <MenuItem value={1}>1 - Highest</MenuItem>
                <MenuItem value={2}>2 - High</MenuItem>
                <MenuItem value={3}>3 - Medium</MenuItem>
                <MenuItem value={4}>4 - Low</MenuItem>
                <MenuItem value={5}>5 - Lowest</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ color: '#b4b9c0' }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// POI Dialog Component
interface POIDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  poi: POI | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const POIDialog: React.FC<POIDialogProps> = ({ open, mode, poi, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    id: '',
    code: '',
    name: '',
    address: '',
    boxLocation: '',
    category: '',
    description: '',
    coordinates: { x: '', y: '', z: '' },
    isActive: true
  });

  useEffect(() => {
    if (mode === 'edit' && poi) {
      setFormData({
        id: poi.id,
        code: poi.code || '',
        name: poi.name,
        address: poi.address,
        boxLocation: poi.boxLocation,
        category: poi.category,
        description: poi.description || '',
        coordinates: {
          x: poi.coordinates?.x?.toString() || '',
          y: poi.coordinates?.y?.toString() || '',
          z: poi.coordinates?.z?.toString() || ''
        },
        isActive: poi.isActive
      });
    } else {
      setFormData({
        id: '',
        code: '',
        name: '',
        address: '',
        boxLocation: '',
        category: '',
        description: '',
        coordinates: { x: '', y: '', z: '' },
        isActive: true
      });
    }
  }, [mode, poi, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      coordinates: {
        x: formData.coordinates.x ? parseFloat(formData.coordinates.x) : null,
        y: formData.coordinates.y ? parseFloat(formData.coordinates.y) : null,
        z: formData.coordinates.z ? parseFloat(formData.coordinates.z) : null
      }
    };
    onSubmit(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: '#eceef1' }}>
        {mode === 'create' ? 'Create POI' : 'Edit POI'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="ID"
              value={formData.id}
              onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value }))}
              required
              disabled={mode === 'edit'}
              sx={{
                '& .MuiInputLabel-root': { color: '#b4b9c0' },
                '& .MuiOutlinedInput-root': {
                  color: '#eceef1',
                  '& fieldset': { borderColor: '#40444b' },
                  '&:hover fieldset': { borderColor: '#3c8cff' },
                  '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                }
              }}
            />
            <TextField
              label="Code (Optional)"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              sx={{
                '& .MuiInputLabel-root': { color: '#b4b9c0' },
                '& .MuiOutlinedInput-root': {
                  color: '#eceef1',
                  '& fieldset': { borderColor: '#40444b' },
                  '&:hover fieldset': { borderColor: '#3c8cff' },
                  '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                }
              }}
            />
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              sx={{
                '& .MuiInputLabel-root': { color: '#b4b9c0' },
                '& .MuiOutlinedInput-root': {
                  color: '#eceef1',
                  '& fieldset': { borderColor: '#40444b' },
                  '&:hover fieldset': { borderColor: '#3c8cff' },
                  '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                }
              }}
            />
            <TextField
              label="Address"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              required
              multiline
              rows={2}
              sx={{
                '& .MuiInputLabel-root': { color: '#b4b9c0' },
                '& .MuiOutlinedInput-root': {
                  color: '#eceef1',
                  '& fieldset': { borderColor: '#40444b' },
                  '&:hover fieldset': { borderColor: '#3c8cff' },
                  '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                }
              }}
            />
            <TextField
              label="Box Location"
              value={formData.boxLocation}
              onChange={(e) => setFormData(prev => ({ ...prev, boxLocation: e.target.value }))}
              required
              sx={{
                '& .MuiInputLabel-root': { color: '#b4b9c0' },
                '& .MuiOutlinedInput-root': {
                  color: '#eceef1',
                  '& fieldset': { borderColor: '#40444b' },
                  '&:hover fieldset': { borderColor: '#3c8cff' },
                  '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                }
              }}
            />
            <TextField
              label="Category"
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              required
              sx={{
                '& .MuiInputLabel-root': { color: '#b4b9c0' },
                '& .MuiOutlinedInput-root': {
                  color: '#eceef1',
                  '& fieldset': { borderColor: '#40444b' },
                  '&:hover fieldset': { borderColor: '#3c8cff' },
                  '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                }
              }}
            />
            <TextField
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              sx={{
                '& .MuiInputLabel-root': { color: '#b4b9c0' },
                '& .MuiOutlinedInput-root': {
                  color: '#eceef1',
                  '& fieldset': { borderColor: '#40444b' },
                  '&:hover fieldset': { borderColor: '#3c8cff' },
                  '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="X Coordinate"
                type="number"
                value={formData.coordinates.x}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  coordinates: { ...prev.coordinates, x: e.target.value }
                }))}
                sx={{
                  '& .MuiInputLabel-root': { color: '#b4b9c0' },
                  '& .MuiOutlinedInput-root': {
                    color: '#eceef1',
                    '& fieldset': { borderColor: '#40444b' },
                    '&:hover fieldset': { borderColor: '#3c8cff' },
                    '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                  }
                }}
              />
              <TextField
                label="Y Coordinate"
                type="number"
                value={formData.coordinates.y}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  coordinates: { ...prev.coordinates, y: e.target.value }
                }))}
                sx={{
                  '& .MuiInputLabel-root': { color: '#b4b9c0' },
                  '& .MuiOutlinedInput-root': {
                    color: '#eceef1',
                    '& fieldset': { borderColor: '#40444b' },
                    '&:hover fieldset': { borderColor: '#3c8cff' },
                    '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                  }
                }}
              />
              <TextField
                label="Z Coordinate"
                type="number"
                value={formData.coordinates.z}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  coordinates: { ...prev.coordinates, z: e.target.value }
                }))}
                sx={{
                  '& .MuiInputLabel-root': { color: '#b4b9c0' },
                  '& .MuiOutlinedInput-root': {
                    color: '#eceef1',
                    '& fieldset': { borderColor: '#40444b' },
                    '&:hover fieldset': { borderColor: '#3c8cff' },
                    '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
                  }
                }}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#3c8cff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#3c8cff'
                      }
                    }
                  }}
                />
              }
              label="Active"
              sx={{ color: '#eceef1' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ color: '#b4b9c0' }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const AdminInterface: React.FC<AdminInterfaceProps> = ({ open, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [rejectedUsers, setRejectedUsers] = useState<RejectedUser[]>([]);
  const [callCodes, setCallCodes] = useState<CallCode[]>([]);
  const [pois, setPOIs] = useState<POI[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [approveDialog, setApproveDialog] = useState<{
    open: boolean;
    user: PendingUser | null;
    selectedRole: string;
  }>({
    open: false,
    user: null,
    selectedRole: 'unit'
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({
    open: false,
    user: null
  });
  const [rebuildDialog, setRebuildDialog] = useState(false);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [callCodeDialog, setCallCodeDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    callCode: CallCode | null;
  }>({
    open: false,
    mode: 'create',
    callCode: null
  });
  const [poiDialog, setPOIDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    poi: POI | null;
  }>({
    open: false,
    mode: 'create',
    poi: null
  });

  useEffect(() => {
    if (open) {
      loadUsers();
      loadCallCodes();
      loadPOIs();
    }
  }, [open]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await http.get('/auth/admin/users');
      if (response.data.success) {
        setUsers(response.data.data.users);
        setPendingUsers(response.data.data.pendingUsers);
        setRejectedUsers(response.data.data.rejectedUsers || []);
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to load users';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async () => {
    if (!approveDialog.user) return;

    try {
      const response = await http.post('/auth/admin/approve-user', {
        username: approveDialog.user.username,
        role: approveDialog.selectedRole
      });

      if (response.data.success) {
        setSuccess('User approved successfully');
        setApproveDialog({ open: false, user: null, selectedRole: 'unit' });
        loadUsers();
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to approve user';
      setError(errorMsg);
    }
  };

  const handleRejectUser = async (username: string) => {
    if (!window.confirm('Are you sure you want to reject this user?')) return;

    try {
      const response = await http.post('/auth/admin/reject-user', {
        username
      });

      if (response.data.success) {
        setSuccess('User rejected successfully');
        loadUsers();
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to reject user';
      setError(errorMsg);
    }
  };

  const handleUpdateUserRole = async (username: string, newRole: string) => {
    try {
      const response = await http.post('/auth/admin/update-user-role', {
        username,
        newRole
      });

      if (response.data.success) {
        setSuccess('User role updated successfully');
        loadUsers();
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to update user role';
      setError(errorMsg);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;

    try {
      const response = await http.post('/auth/admin/delete-user', {
        username: deleteDialog.user.username
      });

      if (response.data.success) {
        setSuccess('User deleted successfully');
        setDeleteDialog({ open: false, user: null });
        loadUsers();
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to delete user';
      setError(errorMsg);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'dispatcher': return 'primary';
      case 'command': return 'warning';
      case 'unit': return 'secondary';
      default: return 'default';
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
  };

  const handleRebuildDatabase = async () => {
    setRebuildLoading(true);
    try {
      const response = await http.post('/config/admin/rebuild-database');
      if (response.data.success) {
        setSuccess('Database rebuilt successfully');
        setRebuildDialog(false);
      } else {
        setError(response.data.message || 'Database rebuild failed');
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to rebuild database';
      setError(errorMsg);
    } finally {
      setRebuildLoading(false);
    }
  };

  const handleFlushCache = async () => {
    try {
      const response = await http.post('/config/admin/flush-cache');
      if (response.data.success) {
        setSuccess('Cache flushed successfully');
      } else {
        setError(response.data.message || 'Cache flush failed');
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to flush cache';
      setError(errorMsg);
    }
  };

  const loadCallCodes = async () => {
    try {
      const response = await http.get('/config/call-codes');
      if (response.data.success) {
        const codesArray = Object.entries(response.data.data).map(([code, data]: [string, any]) => ({
          code,
          description: data.desc,
          boxPlan: data.box,
          priority: data.pri
        }));
        setCallCodes(codesArray);
      }
    } catch (error: any) {
      console.error('Failed to load call codes:', error);
    }
  };

  const loadPOIs = async () => {
    try {
      const response = await http.get('/config/pois');
      if (response.data.success) {
        setPOIs(response.data.data);
      }
    } catch (error: any) {
      console.error('Failed to load POIs:', error);
    }
  };

  const handleCreateCallCode = async (callCodeData: Omit<CallCode, 'code'> & { code: string }) => {
    try {
      const response = await http.post('/config/admin/call-codes', callCodeData);
      if (response.data.success) {
        setSuccess('Call code created successfully');
        setCallCodeDialog({ open: false, mode: 'create', callCode: null });
        loadCallCodes();
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to create call code';
      setError(errorMsg);
    }
  };

  const handleUpdateCallCode = async (code: string, callCodeData: Omit<CallCode, 'code'>) => {
    try {
      const response = await http.put(`/config/admin/call-codes/${code}`, callCodeData);
      if (response.data.success) {
        setSuccess('Call code updated successfully');
        setCallCodeDialog({ open: false, mode: 'edit', callCode: null });
        loadCallCodes();
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to update call code';
      setError(errorMsg);
    }
  };

  const handleDeleteCallCode = async (code: string) => {
    if (!window.confirm(`Are you sure you want to delete call code ${code}?`)) return;

    try {
      const response = await http.delete(`/config/admin/call-codes/${code}`);
      if (response.data.success) {
        setSuccess('Call code deleted successfully');
        loadCallCodes();
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to delete call code';
      setError(errorMsg);
    }
  };

  const handleCreatePOI = async (poiData: Omit<POI, 'id'> & { id: string }) => {
    try {
      const response = await http.post('/config/admin/pois', poiData);
      if (response.data.success) {
        setSuccess('POI created successfully');
        setPOIDialog({ open: false, mode: 'create', poi: null });
        loadPOIs();
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to create POI';
      setError(errorMsg);
    }
  };

  const handleUpdatePOI = async (id: string, poiData: Omit<POI, 'id'>) => {
    try {
      const response = await http.put(`/config/admin/pois/${id}`, poiData);
      if (response.data.success) {
        setSuccess('POI updated successfully');
        setPOIDialog({ open: false, mode: 'edit', poi: null });
        loadPOIs();
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to update POI';
      setError(errorMsg);
    }
  };

  const handleDeletePOI = async (id: string) => {
    if (!window.confirm(`Are you sure you want to delete POI ${id}?`)) return;

    try {
      const response = await http.delete(`/config/admin/pois/${id}`);
      if (response.data.success) {
        setSuccess('POI deleted successfully');
        loadPOIs();
      }
    } catch (error: any) {
      const errorMsg = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to delete POI';
      setError(errorMsg);
    }
  };


  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={false}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '95vw',
          height: '95vh',
          maxWidth: 'none',
          maxHeight: 'none',
          margin: '2.5vh auto',
          backgroundColor: '#101216',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: '#16181c',
        color: '#eceef1',
        borderBottom: '1px solid #40444b',
        fontWeight: 'bold',
        fontSize: '1.5rem'
      }}>
        Admin Panel
        <IconButton onClick={onClose} sx={{ color: '#eceef1' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ 
        p: 0, 
        height: 'calc(95vh - 120px)', 
        overflow: 'auto',
        backgroundColor: '#101216'
      }}>
        <Box sx={{ p: 3 }}>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Pending Users (${pendingUsers.length})`} />
          <Tab label={`Active Users (${users.length})`} />
          <Tab label={`Rejected Users (${rejectedUsers.length})`} />
          <Tab label={`Call Codes (${callCodes.length})`} />
          <Tab label={`POI Locations (${pois.length})`} />
          <Tab label="CSV Management" />
          <Tab label="System Management" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Pending User Approvals</Typography>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadUsers}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ backgroundColor: '#1c1f25' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Roblox Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.robloxName}</TableCell>
                      <TableCell>{user.email || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          color={getStatusColor(user.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() => setApproveDialog({
                            open: true,
                            user,
                            selectedRole: 'unit'
                          })}
                          disabled={user.status === 'rejected'}
                        >
                          <CheckCircle />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleRejectUser(user.username)}
                          disabled={user.status === 'rejected'}
                        >
                          <Cancel />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {pendingUsers.length === 0 && (
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: '#b4b9c0' }}>
                No pending users
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Active Users</Typography>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadUsers}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ backgroundColor: '#1c1f25' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Roblox Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.robloxName}</TableCell>
                      <TableCell>{user.email || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={getRoleColor(user.role) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          color={getStatusColor(user.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={user.role}
                              onChange={(e) => handleUpdateUserRole(user.username, e.target.value)}
                              disabled={user.username === 'admin'} // Prevent changing admin role
                            >
                              <MenuItem value="admin">Admin</MenuItem>
                              <MenuItem value="dispatcher">Dispatcher</MenuItem>
                              <MenuItem value="command">Command</MenuItem>
                              <MenuItem value="unit">Unit</MenuItem>
                            </Select>
                          </FormControl>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => setDeleteDialog({
                              open: true,
                              user
                            })}
                            disabled={user.username === 'admin'} // Prevent deleting admin user
                            title="Delete user"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {users.length === 0 && (
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: '#b4b9c0' }}>
                No active users
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Rejected Users</Typography>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadUsers}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ backgroundColor: '#1c1f25' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Roblox Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Rejected</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rejectedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.robloxName}</TableCell>
                      <TableCell>{user.email || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          color={getStatusColor(user.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell>{user.rejectedAt ? formatDate(user.rejectedAt) : 'N/A'}</TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => setDeleteDialog({
                            open: true,
                            user: {
                              id: user.id,
                              username: user.username,
                              robloxName: user.robloxName,
                              email: user.email,
                              role: 'unit', // Default role for rejected users
                              status: user.status,
                              createdAt: user.createdAt,
                              isActive: false
                            }
                          })}
                          title="Delete rejected user (allows re-registration)"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {rejectedUsers.length === 0 && (
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: '#b4b9c0' }}>
                No rejected users
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Call Codes Management</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadCallCodes}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCallCodeDialog({ open: true, mode: 'create', callCode: null })}
                >
                  Add Call Code
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper} sx={{ backgroundColor: '#1c1f25' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Box Plan</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {callCodes.map((callCode) => (
                    <TableRow key={callCode.code}>
                      <TableCell>
                        <Chip
                          label={callCode.code}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{callCode.description}</TableCell>
                      <TableCell>{callCode.boxPlan}</TableCell>
                      <TableCell>
                        <Chip
                          label={callCode.priority}
                          color={callCode.priority <= 2 ? 'error' : callCode.priority === 3 ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => setCallCodeDialog({ open: true, mode: 'edit', callCode })}
                          title="Edit call code"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteCallCode(callCode.code)}
                          title="Delete call code"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {callCodes.length === 0 && (
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: '#b4b9c0' }}>
                No call codes found
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">POI Locations Management</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadPOIs}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setPOIDialog({ open: true, mode: 'create', poi: null })}
                >
                  Add POI
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper} sx={{ backgroundColor: '#1c1f25' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Box Location</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pois.map((poi) => (
                    <TableRow key={poi.id}>
                      <TableCell>{poi.id}</TableCell>
                      <TableCell>{poi.code || 'N/A'}</TableCell>
                      <TableCell>{poi.name}</TableCell>
                      <TableCell>{poi.address}</TableCell>
                      <TableCell>
                        <Chip
                          label={poi.boxLocation}
                          color="secondary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{poi.category}</TableCell>
                      <TableCell>
                        <Chip
                          label={poi.isActive ? 'Active' : 'Inactive'}
                          color={poi.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => setPOIDialog({ open: true, mode: 'edit', poi })}
                          title="Edit POI"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeletePOI(poi.id)}
                          title="Delete POI"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {pois.length === 0 && (
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: '#b4b9c0' }}>
                No POIs found
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <CSVManagement onDataUpdated={() => {
          loadCallCodes();
          loadPOIs();
        }} />
      </TabPanel>

      <TabPanel value={tabValue} index={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, color: '#eceef1' }}>
              System Management
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card sx={{ backgroundColor: '#1a1d22', border: '1px solid #40444b' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <StorageIcon sx={{ color: '#3c8cff' }} />
                    <Typography variant="h6" sx={{ color: '#eceef1' }}>
                      Cache Management
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#b4b9c0', mb: 2 }}>
                    Clear all cached data to force fresh data loading from the database.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={handleFlushCache}
                    sx={{
                      color: '#eceef1',
                      borderColor: '#40444b',
                      '&:hover': {
                        borderColor: '#3c8cff',
                        backgroundColor: 'rgba(60, 140, 255, 0.1)'
                      }
                    }}
                  >
                    Flush Cache
                  </Button>
                </CardContent>
              </Card>

              <Card sx={{ backgroundColor: '#1a1d22', border: '1px solid #40444b' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <BuildIcon sx={{ color: '#dc5050' }} />
                    <Typography variant="h6" sx={{ color: '#eceef1' }}>
                      Database Rebuild
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#b4b9c0', mb: 2 }}>
                    Rebuild database tables for units, call codes, POIs, and calls. This will delete all active calls, reset incident numbers, and restore default configurations. User data will be preserved.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setRebuildDialog(true)}
                    sx={{
                      color: '#dc5050',
                      borderColor: '#dc5050',
                      '&:hover': {
                        borderColor: '#b84141',
                        backgroundColor: 'rgba(220, 80, 80, 0.1)'
                      }
                    }}
                  >
                    Rebuild Database
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Approve User Dialog */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, user: null, selectedRole: 'unit' })}>
        <DialogTitle>Approve User</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Approve user <strong>{approveDialog.user?.username}</strong> ({approveDialog.user?.robloxName})?
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Assign Role</InputLabel>
            <Select
              value={approveDialog.selectedRole}
              onChange={(e) => setApproveDialog(prev => ({ ...prev, selectedRole: e.target.value }))}
              label="Assign Role"
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="dispatcher">Dispatcher</MenuItem>
              <MenuItem value="command">Command</MenuItem>
              <MenuItem value="unit">Unit</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false, user: null, selectedRole: 'unit' })}>
            Cancel
          </Button>
          <Button onClick={handleApproveUser} variant="contained" color="primary">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#eceef1' }}>Confirm User Deletion</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#b4b9c0', mb: 2 }}>
            Are you sure you want to permanently delete user <strong>{deleteDialog.user?.username}</strong> ({deleteDialog.user?.robloxName})?
          </Typography>
          <Typography sx={{ color: '#dc5050', fontSize: '0.875rem' }}>
            This action cannot be undone. All user data will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })} sx={{ color: '#b4b9c0' }}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setDeleteDialog({ open: false, user: null });
              handleDeleteUser();
            }} 
            variant="contained" 
            color="error"
            sx={{
              backgroundColor: '#dc5050',
              '&:hover': {
                backgroundColor: '#b84141'
              }
            }}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>


      {/* Database Rebuild Confirmation Dialog */}
      <Dialog open={rebuildDialog} onClose={() => setRebuildDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#eceef1' }}>Confirm Database Rebuild</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#b4b9c0', mb: 2 }}>
            Are you sure you want to rebuild the database? This will:
          </Typography>
          <Box sx={{ ml: 2, mb: 2 }}>
            <Typography sx={{ color: '#b4b9c0', fontSize: '0.875rem' }}>
              • Rebuild call codes from config
            </Typography>
            <Typography sx={{ color: '#b4b9c0', fontSize: '0.875rem' }}>
              • Rebuild POIs from config
            </Typography>
            <Typography sx={{ color: '#b4b9c0', fontSize: '0.875rem' }}>
              • Rebuild units to default state
            </Typography>
            <Typography sx={{ color: '#b4b9c0', fontSize: '0.875rem' }}>
              • Clear all calls and reset incident numbers
            </Typography>
            <Typography sx={{ color: '#b4b9c0', fontSize: '0.875rem' }}>
              • Rebuild dispatcher data
            </Typography>
            <Typography sx={{ color: '#b4b9c0', fontSize: '0.875rem' }}>
              • Flush all caches
            </Typography>
          </Box>
          <Typography sx={{ color: '#dc5050', fontSize: '0.875rem', fontWeight: 'bold' }}>
            ⚠️ All active calls will be deleted and incident numbers will reset to 1. User data will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setRebuildDialog(false)} 
            sx={{ color: '#b4b9c0' }}
            disabled={rebuildLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRebuildDatabase} 
            variant="contained" 
            color="error"
            disabled={rebuildLoading}
            sx={{
              backgroundColor: '#dc5050',
              '&:hover': {
                backgroundColor: '#b84141'
              }
            }}
          >
            {rebuildLoading ? 'Rebuilding...' : 'Rebuild Database'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Call Code Dialog */}
      <CallCodeDialog
        open={callCodeDialog.open}
        mode={callCodeDialog.mode}
        callCode={callCodeDialog.callCode}
        onClose={() => setCallCodeDialog({ open: false, mode: 'create', callCode: null })}
        onSubmit={callCodeDialog.mode === 'create' ? handleCreateCallCode : (data) => handleUpdateCallCode(callCodeDialog.callCode!.code, data)}
      />

      {/* POI Dialog */}
      <POIDialog
        open={poiDialog.open}
        mode={poiDialog.mode}
        poi={poiDialog.poi}
        onClose={() => setPOIDialog({ open: false, mode: 'create', poi: null })}
        onSubmit={poiDialog.mode === 'create' ? handleCreatePOI : (data) => handleUpdatePOI(poiDialog.poi!.id, data)}
      />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AdminInterface;
